import React, { useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Copy, Mail, PenLine } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const TEMPLATES = [
  {
    key: "intro_call",
    label: "Schedule an intro call",
    subject: "Thank you — quick intro call?",
    body: (c) => `Hi${c.name ? " " + c.name.split(" ")[0] : ""},

Thank you so much for getting back to us! We'd love to set up a brief 10-15 minute introduction call with your team so you know exactly who we are and how our referral process works.

Would any time this week or next work for you? We're happy to work around your schedule.

Caregivers Alliance Homecare is a DODD-certified provider, now accepting new clients, and we verify eligibility and authorization before services ever begin.

Warm regards,
Caregivers Alliance Homecare
(614) 526-1548 · info@myohcare.com`,
  },
  {
    key: "referral_info",
    label: "Send referral next steps",
    subject: "Referral next steps — Caregivers Alliance Homecare",
    body: (c) => `Hi${c.name ? " " + c.name.split(" ")[0] : ""},

Wonderful to hear from you! Here's everything your team needs to send us a referral:

• Online referral form: https://myohcare.com/refer
• Provider one-pager: https://myohcare.com/provider-sheet
• Phone: (614) 526-1548 (we respond same business day)

Once a referral arrives, we verify eligibility and authorization with the plan or county board, then coordinate scheduling and caregiver matching with your team.

Thank you for thinking of us — we take great care of every referral.

Warm regards,
Caregivers Alliance Homecare`,
  },
  {
    key: "thank_you",
    label: "Thank you + capacity confirmation",
    subject: "Thank you — and yes, we're accepting new clients",
    body: (c) => `Hi${c.name ? " " + c.name.split(" ")[0] : ""},

Thank you for your reply! Confirming that Caregivers Alliance Homecare is actively accepting new clients${c.county ? `, including ${c.county} County` : ""}.

Services include Homemaker Personal Care (HPC), homemaker support, companionship, respite for family caregivers, and transportation support. We work with DODD waivers, coordinate with MyCare Ohio plans, and offer private-pay options.

Is there anything specific your team needs from us — documentation, rates, or a meet-and-greet?

Warm regards,
Caregivers Alliance Homecare
(614) 526-1548 · info@myohcare.com`,
  },
];

export const ReplyTemplates = ({ contact, onClose }) => {
  const { toast } = useToast();
  const [tpl, setTpl] = useState(TEMPLATES[0]);
  const [text, setText] = useState(TEMPLATES[0].body(contact));

  const pick = (t) => { setTpl(t); setText(t.body(contact)); };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Draft copied", description: "Paste it into your email reply." });
    } catch {
      toast({ title: "Copy failed — select the text manually", variant: "destructive" });
    }
  };

  const mailto = `mailto:${contact.email || ""}?subject=${encodeURIComponent(tpl.subject)}&body=${encodeURIComponent(text)}`;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="reply-templates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0E2A33]">Reply draft — {contact.org}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2 flex-wrap">
          {TEMPLATES.map((t) => (
            <button key={t.key} onClick={() => pick(t)} data-testid={`tpl-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${tpl.key === t.key ? "bg-[#0E5A72] text-white border-[#0E5A72]" : "border-slate-200 text-slate-600 hover:border-[#0E5A72]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Textarea data-testid="reply-draft-text" value={text} onChange={(e) => setText(e.target.value)} rows={14} className="text-sm" />
        <div className="flex gap-2 justify-end">
          <Button data-testid="copy-draft-btn" variant="outline" onClick={copyDraft} className="rounded-full">
            <Copy className="mr-2 h-4 w-4" /> Copy draft
          </Button>
          {contact.email && (
            <a href={mailto}>
              <Button data-testid="open-email-btn" className="rounded-full bg-[#2E9E6A] hover:bg-[#23875A] text-white">
                <Mail className="mr-2 h-4 w-4" /> Open in email
              </Button>
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ReplyDraftButton = ({ contact }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button data-testid="reply-draft-btn" size="sm" variant="outline" onClick={() => setOpen(true)}
        className="h-7 rounded-full text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50">
        <PenLine className="mr-1.5 h-3 w-3" /> Reply draft
      </Button>
      {open && <ReplyTemplates contact={contact} onClose={() => setOpen(false)} />}
    </>
  );
};
